/**
 * ShowPage<T> — a generic Inspector-grammar show template resource pages can
 * adopt instead of hand-rolling each screen (design-system §5, the "no bespoke
 * per-entity screens" law).
 *
 * It owns the boilerplate every show page shares: the `useShow` fetch, the
 * height-matched loading skeleton (never a spinner for content, §7), the calm
 * inline error state with a Retry, and the `Inspector` + `InspectorHeader`
 * scaffold. The caller supplies *what* the entity shows via render props /
 * config — Overview fields, an optional Body, Relations groups, and the
 * provenance refs for the Audit block — and the template arranges them in the
 * canonical Header / Overview / Body / Relations / Audit order.
 *
 * Resources with bespoke needs can instead compose the lower-level pieces
 * (`Inspector`, `InspectorHeader`, `InspectorSection`, `FieldGrid`,
 * `RelationsBlock`, `AuditBlock`) directly; this template is the easy default.
 */
import * as React from "react";
import { useShow, type BaseRecord, type HttpError } from "@refinedev/core";

import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/ui/states";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  TechnicalId,
  type InspectorAction,
} from "@/components/inspector/Inspector";
import { Field, FieldGrid } from "@/components/inspector/FieldGrid";
import {
  RelationsBlock,
  type RelationGroup,
} from "@/components/inspector/RelationsBlock";
import { AuditBlock, type ProvenanceRef } from "@/components/inspector/AuditBlock";

/** Height-matched skeleton for a show page (FieldGrid bars, never a spinner). */
export function ShowPageSkeleton() {
  return (
    <div className="max-w-5xl space-y-8">
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-3 w-20" />
        <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="grid grid-cols-[10rem_1fr] gap-x-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-full max-w-[12rem]" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Calm inline error state with a Retry (toasts are for actions, not loads §7).
 * Delegates to the shared `ErrorState` so every load failure — list, panel, or
 * detail page — renders the same calm block; constrained to the detail width.
 */
export function ShowPageError({
  message,
  onRetry,
  className,
}: {
  message?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <ErrorState
      message={message ?? "Something went wrong while loading this record."}
      onRetry={onRetry}
      className={["max-w-5xl", className].filter(Boolean).join(" ")}
    />
  );
}

export interface ShowPageProps<T extends BaseRecord> {
  /** Resource path (Refine `name`). Drives both `useShow` and the Audit fetch. */
  resource: string;
  /** Explicit id; defaults to the route id resolved by `useShow`. */
  id?: string | number;

  /** Title from the loaded record (defaults to `title`/`name`/id heuristics). */
  title?: (record: T) => React.ReactNode;
  /**
   * One-line header subtitle (feedback #6 — every show page reads with the same
   * title + subheader structure). Static string or derived from the record.
   */
  subtitle?: string | ((record: T) => React.ReactNode);
  /** Status string from the record for the Header `StatusBadge`. */
  status?: (record: T) => string | null | undefined;

  /** The single primary (filled accent) action. */
  primaryAction?: (record: T) => React.ReactNode;
  /** Inline ghost actions, left of the overflow menu. */
  actions?: (record: T) => React.ReactNode;
  /** Overflow actions for the trailing `…` menu (e.g. Edit, Delete). */
  menuActions?: (record: T) => InspectorAction[];

  /** Overview FieldGrid contents (the canonical fields, each `*_id` a RefLink). */
  overview: (record: T) => React.ReactNode;
  /**
   * Optional muted "Technical" FieldGrid (ids / source / timestamps). Rendered
   * after Relations as a demoted tail per the field-prioritization convention
   * (frontend-followup §E). Omit when an entity has no technical tail to show.
   */
  technical?: (record: T) => React.ReactNode;
  /** Optional Body (markdown / json / prose). Omit when the entity has none. */
  body?: (record: T) => React.ReactNode;
  /** Relations subsections (References / Referenced by). */
  relations?: (record: T) => RelationGroup[];
  /** Provenance refs for the Audit block's causal-id row. */
  provenance?: (record: T) => ProvenanceRef[];
  /** Hide the Audit section entirely (rare — kernel atoms like Event are self). */
  hideAudit?: boolean;
}

function defaultTitle(record: BaseRecord): React.ReactNode {
  const r = record as Record<string, unknown>;
  for (const f of ["title", "name", "label", "display_name", "summary"]) {
    const v = r[f];
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return String(record.id ?? "Record");
}

/**
 * Generic Inspector-grammar show page. Loads the record, renders the canonical
 * Header / Overview / Body / Relations / Audit sections from the supplied
 * config, and degrades to a skeleton (loading) or a calm Retry (error).
 */
export function ShowPage<T extends BaseRecord = BaseRecord>(
  props: ShowPageProps<T>,
) {
  const {
    resource,
    id,
    title,
    subtitle,
    status,
    primaryAction,
    actions,
    menuActions,
    overview,
    technical,
    body,
    relations,
    provenance,
    hideAudit,
  } = props;

  const { query } = useShow<T, HttpError>({ resource, id });
  const { data, isLoading, isError, error, refetch } = query;
  const record = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !record) {
    return (
      <ShowPageError
        message={error?.message}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const relationGroups = relations?.(record) ?? [];
  const provRefs = provenance?.(record);
  const bodyNode = body?.(record);
  const technicalNode = technical?.(record);
  const recordId = record.id !== undefined ? String(record.id) : undefined;
  const subtitleNode =
    typeof subtitle === "function" ? subtitle(record) : subtitle;

  return (
    <Inspector>
      <InspectorHeader
        title={title ? title(record) : defaultTitle(record)}
        subtitle={subtitleNode}
        status={status ? status(record) : undefined}
        primaryAction={primaryAction?.(record)}
        actions={actions?.(record)}
        menuActions={menuActions?.(record)}
      />

      <InspectorSection title="Overview">
        <FieldGrid>{overview(record)}</FieldGrid>
      </InspectorSection>

      {bodyNode ? (
        <InspectorSection title="Body">{bodyNode}</InspectorSection>
      ) : null}

      {relationGroups.length > 0 ? (
        <InspectorSection title="Relations">
          <RelationsBlock groups={relationGroups} />
        </InspectorSection>
      ) : null}

      {technicalNode || recordId ? (
        <InspectorSection title="Technical">
          <FieldGrid className="text-muted-foreground">
            {recordId ? (
              <Field label="Id">
                <TechnicalId id={recordId} />
              </Field>
            ) : null}
            {technicalNode}
          </FieldGrid>
        </InspectorSection>
      ) : null}

      {!hideAudit ? (
        <InspectorSection title="Audit">
          <AuditBlock
            entityType={resource}
            entityId={record.id}
            provenance={provRefs}
          />
        </InspectorSection>
      ) : null}
    </Inspector>
  );
}

export default ShowPage;
